import {
  Box,
  BoxProps,
  Flex,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  ModalProps,
  useDisclosure,
} from '@chakra-ui/react';
import * as React from 'react';

import { ZoomInIcon, ZoomOutIcon } from '@/imports/ui/chakra/feather';

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.5;

export type ImagePreviewModalProps = {
  src?: string | null;
  alt: string;
  isOpen: boolean;
  onClose: () => void;
  size?: ModalProps['size'];
  maxH?: string;
  fallbackPreview?: React.ReactNode;
  enableZoom?: boolean;
};

export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  src,
  alt,
  isOpen,
  onClose,
  size = 'lg',
  maxH = '55vh',
  fallbackPreview,
  enableZoom = false,
}) => {
  const [zoom, setZoom] = React.useState(MIN_ZOOM);

  React.useEffect(() => {
    if (!isOpen) {
      setZoom(MIN_ZOOM);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={size}>
      <ModalOverlay />
      <ModalContent>
        <Flex justify="flex-end" align="center" px="2" pt="2">
          {enableZoom && src && (
            <>
              <IconButton
                aria-label="Zoom out"
                icon={<ZoomOutIcon />}
                size="sm"
                variant="ghost"
                colorScheme="gray"
                isDisabled={zoom <= MIN_ZOOM}
                onClick={() =>
                  setZoom((current) => Math.max(MIN_ZOOM, current - ZOOM_STEP))
                }
              />
              <IconButton
                aria-label="Zoom in"
                icon={<ZoomInIcon />}
                size="sm"
                variant="ghost"
                colorScheme="gray"
                isDisabled={zoom >= MAX_ZOOM}
                onClick={() =>
                  setZoom((current) => Math.min(MAX_ZOOM, current + ZOOM_STEP))
                }
              />
            </>
          )}
          <ModalCloseButton position="static" />
        </Flex>
        <ModalBody px="6" pt="2" pb="6">
          {src ? (
            <Box overflow="auto" maxH={maxH} w="full">
              <Image
                src={src}
                alt={alt}
                display="block"
                mx="auto"
                w={`${zoom * 100}%`}
                maxW={zoom === MIN_ZOOM ? '100%' : 'none'}
                maxH={zoom === MIN_ZOOM ? maxH : undefined}
                objectFit="contain"
              />
            </Box>
          ) : (
            <Flex justify="center" py="4">
              {fallbackPreview}
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export type ImagePreviewProps = {
  src?: string | null;
  alt: string;
  size?: ModalProps['size'];
  maxH?: string;
  fallbackPreview?: React.ReactNode;
  enableZoom?: boolean;
  children: React.ReactNode;
} & Omit<BoxProps, 'as' | 'onClick' | 'aria-label' | 'children' | 'role'>;

export const ImagePreview: React.FC<ImagePreviewProps> = ({
  src,
  alt,
  size,
  maxH,
  fallbackPreview,
  enableZoom,
  children,
  ...triggerProps
}) => {
  const preview = useDisclosure();
  const canPreview = Boolean(src) || fallbackPreview != null;

  if (!canPreview) {
    return <>{children}</>;
  }

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        position="relative"
        zIndex="1"
        cursor="pointer"
        bg="transparent"
        p="0"
        flexShrink={0}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          preview.onOpen();
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          preview.onOpen();
        }}
        aria-label={`Preview ${alt}`}
        {...triggerProps}
      >
        {children}
      </Box>
      <ImagePreviewModal
        src={src}
        alt={alt}
        isOpen={preview.isOpen}
        onClose={preview.onClose}
        size={size}
        maxH={maxH}
        fallbackPreview={fallbackPreview}
        enableZoom={enableZoom}
      />
    </>
  );
};
